/**
 * Gateway Service — Drop-in wrapper for supabase.functions.invoke()
 *
 * When VITE_USE_GATEWAY=true, calls go to the Express gateway (api.flusia.it)
 * instead of Supabase Edge Functions. The gateway handles them directly when
 * the corresponding HANDLE_* env var is set, otherwise proxies to Supabase.
 *
 * Usage (drop-in replacement for supabase.functions.invoke):
 *
 *   import { gatewayCall, gatewaySSE } from '@/lib/services/gatewayService';
 *
 *   // Instead of: const { data, error } = await supabase.functions.invoke('send-email', { body: {...} });
 *   // Use:        const { data, error } = await gatewayCall('send-email', { body: {...} });
 *
 *   // For SSE streaming:
 *   const controller = gatewaySSE('ai-help-chat', { body: {...} }, {
 *     onData: (chunk) => console.log(chunk),
 *     onDone: () => console.log('done'),
 *     onError: (err) => console.error(err),
 *   });
 *   // To abort: controller.abort();
 */

import { supabase } from '@/lib/supabase';

import { isCoreOnlyRoute, parseCoreError } from '../commerce/coreRouting';
import { AI_DISABLED_CODE, AI_DISABLED_MESSAGE, notifyAiDisabled } from '../ai/aiDisabled';

const GATEWAY_URL = import.meta.env.VITE_API_GATEWAY_URL || 'https://api.flusia.it';
const USE_GATEWAY = import.meta.env.VITE_USE_GATEWAY === 'true';

// flusia-core (TS backend, business logic Wave 2/3/5/6/7/8) — vedi plan splendid-nibbling-gizmo.md
const CORE_URL = import.meta.env.VITE_CORE_URL || 'https://core.flusia.it';
const USE_CORE = import.meta.env.VITE_USE_CORE === 'true';
// New customer-order endpoints have no legacy Edge equivalent.
// Le rotte del motore e-commerce (commerce/*, admin/commerce/*) vanno sempre sul core: regola a
// prefisso in isCoreOnlyRoute (12 R-09), perche' contengono id e non possono stare in questo Set.
const CORE_ONLY_ROUTES = new Set(['account-deletion', 'account-deletion/cancel', 'account-deletion/mfa', 'newsletter-template-test', 'settings/ai', 'orders/manual', 'orders/warehouse-document', 'warehouse/stock/inbound', 'warehouse/stock/outbound', 'warehouse/preparation/complete', 'warehouse/inventory', 'warehouse/outbound/delivered', 'warehouse/outbound/save', 'warehouse/returns/register', 'warehouse/returns/restock-line',
  // 2FA: dispositivi ricordati e codici di emergenza, solo sul core (le edge omonime sono state tolte il 17/10/2026)
  'twofa-trusted-device-login', 'twofa-trusted-devices', 'twofa-backup-codes']);
// Custom dev header per CORS hardening localhost (QA12 defense-in-depth)
const CORE_DEV_KEY = import.meta.env.VITE_GATEWAY_DEV_KEY as string | undefined;

interface InvokeOptions {
  body?: Record<string, any> | any;
  headers?: Record<string, string>;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
}

interface InvokeResult<T = any> {
  data: T | null;
  /** code e status solo da coreCall: codice stabile del core (es. job_already_running) e stato HTTP. */
  error: { message: string; code?: string | null; status?: number } | null;
}

/**
 * Call an edge function — either via gateway or directly via Supabase.
 * Same interface as supabase.functions.invoke().
 */
export async function gatewayCall<T = any>(
  functionName: string,
  options?: InvokeOptions
): Promise<InvokeResult<T>> {
  if (!USE_GATEWAY) {
    // Fallback: use Supabase Edge Functions directly
    return supabase.functions.invoke(functionName, options) as Promise<InvokeResult<T>>;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      return { data: null, error: { message: 'Not authenticated' } };
    }

    const response = await fetch(`${GATEWAY_URL}/api/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options?.headers,
      },
      body: options?.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      let errorMessage: string;
      try {
        const parsed = JSON.parse(errorBody);
        errorMessage = parsed.error || parsed.message || `HTTP ${response.status}`;
      } catch {
        errorMessage = errorBody || `HTTP ${response.status}`;
      }
      // Interruttore IA dell'azienda spento: toast unico e messaggio leggibile.
      if (errorMessage === AI_DISABLED_CODE) {
        notifyAiDisabled();
        return { data: null, error: { message: AI_DISABLED_MESSAGE, code: AI_DISABLED_CODE, status: response.status } };
      }
      return { data: null, error: { message: errorMessage } };
    }

    const contentType = response.headers.get('content-type') || '';
    let data: T;
    if (contentType.includes('application/json')) {
      data = await response.json() as T;
    } else {
      data = await response.text() as unknown as T;
    }

    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: { message: err.message || 'Gateway call failed' } };
  }
}

/**
 * SSE streaming call to an edge function via the gateway.
 * Returns an AbortController to cancel the stream.
 */
export interface SSECallbacks {
  onData?: (chunk: string) => void;
  onEvent?: (event: string, data: any) => void;
  onDone?: () => void;
  onError?: (error: string) => void;
}

export function gatewaySSE(
  functionName: string,
  options: InvokeOptions,
  callbacks: SSECallbacks
): AbortController {
  const controller = new AbortController();

  (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        callbacks.onError?.('Not authenticated');
        return;
      }

      const response = await fetch(`${GATEWAY_URL}/api/${functionName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        body: options?.body ? JSON.stringify(options.body) : undefined,
        signal: controller.signal,
      });

      if (!response.ok) {
        callbacks.onError?.(`HTTP ${response.status}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        callbacks.onError?.('No response body');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = 'message';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const dataStr = line.slice(6);
            try {
              const parsed = JSON.parse(dataStr);
              callbacks.onEvent?.(currentEvent, parsed);
            } catch {
              callbacks.onData?.(dataStr);
            }
            if (currentEvent === 'done') {
              callbacks.onDone?.();
            } else if (currentEvent === 'error') {
              callbacks.onError?.(dataStr);
            }
          } else if (line.startsWith(': ')) {
            // heartbeat comment, ignore
          }
        }
      }

      callbacks.onDone?.();
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        callbacks.onError?.(err.message || 'SSE connection failed');
      }
    }
  })();

  return controller;
}

// ─────────────────────────────────────────────────────────────────────────────
// flusia-core (core.flusia.it) — business logic backend
// Pattern speculare a gatewayCall ma punta a `core.flusia.it` invece di `api.flusia.it`.
// Default VITE_USE_CORE=false → fallback a Supabase Edge (transitional path durante migration).
// Quando VITE_USE_CORE=true, le chiamate vanno a flusia-core; lì routeFlags.shouldHandle()
// decide se processare locale o proxy a Supabase Edge.
// ─────────────────────────────────────────────────────────────────────────────

export async function coreCall<T = any>(
  functionName: string,
  options?: InvokeOptions
): Promise<InvokeResult<T>> {
  if (!USE_CORE && !isCoreOnlyRoute(functionName, CORE_ONLY_ROUTES)) {
    // Fallback: Supabase Edge Functions (status quo finché Wave 3 non rolloutata)
    return supabase.functions.invoke(functionName, options) as Promise<InvokeResult<T>>;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      return { data: null, error: { message: 'Not authenticated', code: 'not_authenticated' } };
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options?.headers,
    };
    if (CORE_DEV_KEY) headers['X-Flusia-Dev-Key'] = CORE_DEV_KEY;

    const response = await fetch(`${CORE_URL}/api/${functionName}`, {
      method: options?.method ?? 'POST',
      headers,
      body: options?.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
      // Forma del core { error: { type, code, message } } o forme vecchie: il codice resta (16 sez. 3.1)
      const info = parseCoreError(await response.text(), response.status);
      // Interruttore IA dell'azienda spento: toast unico in tutto il CRM.
      if (info.code === AI_DISABLED_CODE) notifyAiDisabled();
      return { data: null, error: { message: info.message, code: info.code, status: info.status } };
    }

    const contentType = response.headers.get('content-type') || '';
    let data: T;
    if (contentType.includes('application/json')) {
      data = await response.json() as T;
    } else {
      data = await response.text() as unknown as T;
    }

    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: { message: err.message || 'Core call failed', code: 'network_error' } };
  }
}

/**
 * Variant of coreCall for binary responses (PDF, DOCX, images).
 * Returns Blob instead of parsed JSON/text.
 * Throws Error on non-2xx response.
 */
export async function coreCallBlob(
  functionName: string,
  options?: InvokeOptions
): Promise<Blob> {
  if (!USE_CORE) {
    throw new Error('coreCallBlob requires VITE_USE_CORE=true');
  }

  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new Error('Not authenticated');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options?.headers,
  };
  if (CORE_DEV_KEY) headers['X-Flusia-Dev-Key'] = CORE_DEV_KEY;

  const response = await fetch(`${CORE_URL}/api/${functionName}`, {
    method: 'POST',
    headers,
    body: options?.body ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    const errBody = await response.text();
    let errMsg: string;
    try {
      const parsed = JSON.parse(errBody);
      errMsg = parsed.error?.message ?? parsed.error ?? parsed.message ?? `HTTP ${response.status}`;
    } catch {
      errMsg = errBody || `HTTP ${response.status}`;
    }
    throw new Error(errMsg);
  }

  return await response.blob();
}

export function coreSSE(
  functionName: string,
  options: InvokeOptions,
  callbacks: SSECallbacks
): AbortController {
  const controller = new AbortController();

  (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        callbacks.onError?.('Not authenticated');
        return;
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
      };
      if (CORE_DEV_KEY) headers['X-Flusia-Dev-Key'] = CORE_DEV_KEY;

      const response = await fetch(`${CORE_URL}/api/${functionName}`, {
        method: 'POST',
        headers,
        body: options?.body ? JSON.stringify(options.body) : undefined,
        signal: controller.signal,
      });

      if (!response.ok) {
        callbacks.onError?.(`HTTP ${response.status}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        callbacks.onError?.('No response body');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = 'message';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const dataStr = line.slice(6);
            try {
              const parsed = JSON.parse(dataStr);
              callbacks.onEvent?.(currentEvent, parsed);
            } catch {
              callbacks.onData?.(dataStr);
            }
            if (currentEvent === 'done') {
              callbacks.onDone?.();
            } else if (currentEvent === 'error') {
              callbacks.onError?.(dataStr);
            }
          }
        }
      }

      callbacks.onDone?.();
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        callbacks.onError?.(err.message || 'Core SSE connection failed');
      }
    }
  })();

  return controller;
}
